import { useMemo, useState } from 'react';

import { useAdminAuditGetAdminAudit } from '@/lib/api/admin-audit/admin-audit';
import type { AuditEntryDto } from '@/lib/api/schemas';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const EVENT_CHIPS = [
  { key: '', label: 'All events' },
  { key: 'client.created', label: 'Client created' },
  { key: 'client.updated', label: 'Client updated' },
  { key: 'client.suspended', label: 'Client suspended' },
  { key: 'client.activated', label: 'Client activated' },
  { key: 'client.deleted', label: 'Client deleted' },
  { key: 'client.secret_rotated', label: 'Secret rotated' },
  { key: 'client.secret_revoked', label: 'Secret revoked' },
  { key: 'admin_user.created', label: 'Admin user created' },
  { key: 'admin_user.updated', label: 'Admin user updated' },
  { key: 'admin_user.deleted', label: 'Admin user deleted' },
];

export default function AuditPage() {
  const [eventType, setEventType] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [page, setPage] = useState(1);

  const query = useAdminAuditGetAdminAudit({
    page,
    limit: 50,
    eventType: eventType || undefined,
    performedBy: performedBy || undefined,
  });

  const grouped = useMemo(() => groupByDay(query.data?.items ?? []), [query.data]);

  return (
    <section className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Audit trail</h1>
        <p className="text-sm text-muted-foreground">
          Append-only log of every admin mutation. Diffs show the before / after state.
        </p>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {EVENT_CHIPS.map((chip) => (
              <button
                key={chip.key || 'all'}
                type="button"
                onClick={() => {
                  setEventType(chip.key);
                  setPage(1);
                }}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  eventType === chip.key
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                )}
              >
                {chip.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <Input
                value={performedBy}
                placeholder="Performed by (email)"
                onChange={(e) => setPerformedBy(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setPage(1);
                }}
                className="w-56"
              />
              {performedBy && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPerformedBy('');
                    setPage(1);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {query.isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            {(query.error as Error)?.message ?? 'Could not load audit trail.'}
          </CardContent>
        </Card>
      ) : query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No audit events match the current filter.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ day, items }) => (
            <div key={day} className="space-y-2">
              <h2 className="sticky top-0 z-10 -mx-2 bg-background/90 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">
                {day}
              </h2>
              <ol className="space-y-2 border-l border-border pl-4">
                {items.map((entry) => (
                  <AuditItem key={entry.id} entry={entry} />
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {query.data && query.data.total > query.data.limit && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {query.data.items.length} of {query.data.total} events
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function AuditItem({ entry }: { entry: AuditEntryDto }) {
  const [expanded, setExpanded] = useState(false);
  const hasDiff = Boolean(entry.oldValuesJson || entry.newValuesJson);

  return (
    <li className="-ml-[17px] rounded-md border border-border bg-card p-3 text-sm">
      <div className="flex items-start gap-3">
        <span className="mt-1 h-2 w-2 rounded-full bg-primary" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{entry.eventType}</Badge>
            <span className="text-xs text-muted-foreground">{entry.entityType}</span>
            {entry.entityId && (
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-[10px] text-muted-foreground">
                {entry.entityId}
              </code>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{entry.performedBy}</span>{' '}
            · {new Date(entry.createdAt).toLocaleTimeString([], { hour12: false })}
          </div>
        </div>
        {hasDiff && (
          <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Hide diff' : 'View diff'}
          </Button>
        )}
      </div>
      {expanded && hasDiff && (
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          <DiffPane label="Before" json={entry.oldValuesJson ?? null} kind="old" />
          <DiffPane label="After" json={entry.newValuesJson ?? null} kind="new" />
        </div>
      )}
    </li>
  );
}

function DiffPane({ label, json, kind }: { label: string; json: string | null; kind: 'old' | 'new' }) {
  const tone =
    kind === 'old' ? 'border-destructive/40 bg-destructive/5' : 'border-emerald-500/40 bg-emerald-500/5';
  return (
    <div className={cn('rounded-md border p-2', tone)}>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      {json ? (
        <pre className="whitespace-pre-wrap break-all font-mono text-[11px]">{pretty(json)}</pre>
      ) : (
        <div className="text-[11px] italic text-muted-foreground">—</div>
      )}
    </div>
  );
}

function pretty(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function groupByDay(items: AuditEntryDto[]): { day: string; items: AuditEntryDto[] }[] {
  const groups = new Map<string, AuditEntryDto[]>();
  for (const entry of items) {
    const day = new Date(entry.createdAt).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const existing = groups.get(day);
    if (existing) existing.push(entry);
    else groups.set(day, [entry]);
  }
  return [...groups.entries()].map(([day, items]) => ({ day, items }));
}
